import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Eye,
  History,
  Loader2,
  RotateCcw,
  Server,
  TriangleAlert,
  User,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  workflowsApi,
  type WorkflowVersion,
  type WorkflowVersionRow,
} from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { useTenant } from "@/context/tenant-context";
import { cn } from "@/lib/utils";

/**
 * Version history for a workflow, with restore.
 *
 * Exists because nodes/edges are overwritten wholesale on every save — by a
 * human on the canvas or by an agent calling update_workflow — and there was
 * previously no way back.
 *
 * Restoring is not destructive: the current state was itself snapshotted on
 * its way in, and the restore records a new version, so nothing is lost by
 * trying one. The confirm step exists because a restore replaces what's on
 * the canvas right now, which is startling if unintended — not because it's
 * irreversible.
 *
 * Layout (2026-10-09): one bordered box per day with divider rows, like the
 * Tasks list, instead of a card per version. Each row leads with what changed;
 * the workflow name, author and node count only show when they differ from
 * the version before, because repeating them on every row hid the change.
 */
export function VersionHistoryDialog({
  workflowId,
  onClose,
  onRestored,
  onPreview,
  previewingVersionId,
}: {
  workflowId: string;
  onClose: () => void;
  onRestored: () => void;
  /** The version already open on the canvas, so the list can mark it. */
  previewingVersionId?: string | null;
  /**
   * Show this version on the canvas without writing anything. Looking at an
   * old graph and committing to it are different decisions, and the list
   * alone — node counts and a one-line summary — is not enough to make the
   * second one.
   */
  onPreview: (version: WorkflowVersion) => void;
}) {
  const queryClient = useQueryClient();
  // History is readable by everyone; restoring needs agent admin or above on
  // the server, so standard users get View only (2026-09-30).
  const { canManageAnyAgent: canRestore } = useTenant();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: (versionId: string) =>
      workflowsApi.getVersion(workflowId, versionId),
    onSuccess: (version) => {
      setLoadingId(null);
      onPreview(version);
      onClose();
    },
    onError: (err: unknown) => {
      setLoadingId(null);
      setError(err instanceof Error ? err.message : "Could not open that version");
    },
  });

  const {
    data: versions,
    isLoading,
    isError,
    error: listError,
  } = useQuery({
    queryKey: queryKeys.workflows.versions(workflowId),
    queryFn: () => workflowsApi.listVersions(workflowId),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) =>
      workflowsApi.restoreVersion(workflowId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.detail(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.versions(workflowId),
      });
      onRestored();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not restore");
    },
  });

  const days = versions ? groupByDay(versions) : [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-[min(720px,92vw)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header stays put while the list scrolls under it. */}
        <div className="shrink-0 border-b border-border px-5 pb-4 pt-5">
          <div className="flex items-center gap-2">
            <History size={16} className="text-accent" />
            <h2 className="text-[15px] font-semibold text-text-primary">Version History</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
            >
              <X size={15} />
            </button>
          </div>
          <p className="mt-1 text-xs text-text-muted">
            Every save from the canvas or an agent is kept. Restoring an old
            version saves it as a new one, so nothing is lost.
          </p>
          {versions && versions.length > 0 && <CreatedAndEdited versions={versions} />}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={18} className="animate-spin text-text-muted" />
            </div>
          ) : isError ? (
            /* A failed request used to render as "No history yet", so a broken
               endpoint looked exactly like a workflow nobody had saved twice.
               Never let those two say the same thing. */
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-3 py-6 text-center">
              <p className="text-sm font-medium text-text-primary">
                History could not be loaded
              </p>
              <p className="mt-1 break-words text-xs text-text-muted">
                {listError instanceof Error
                  ? listError.message
                  : "The server did not return the version list."}
              </p>
              <p className="mt-2 text-xs text-text-muted">
                This is not the same as having no history. Nothing has been lost.
              </p>
            </div>
          ) : !versions || versions.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-surface/50 px-3 py-8 text-center">
              <p className="text-sm font-medium text-text-primary">
                No history yet
              </p>
              <p className="mt-1 text-xs text-text-muted">
                The next save will create the first version. History only covers
                changes made after this feature shipped.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {days.map((day) => (
                <section key={day.key}>
                  <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
                    {day.label} <span className="font-normal opacity-70">· {day.rows.length}</span>
                  </p>
                  <ol className="overflow-hidden rounded-lg border border-border bg-surface">
                    {day.rows.map(({ version: v, older, firstOfDay }) => (
                      <VersionRow
                        key={v.id}
                        version={v}
                        older={older}
                        firstOfDay={firstOfDay}
                        isCurrent={v.id === versions[0]!.id}
                        isViewing={previewingVersionId === v.id}
                        isConfirming={confirmId === v.id}
                        isPending={restore.isPending && confirmId === v.id}
                        isOpening={loadingId === v.id}
                        onView={() => {
                          setError(null);
                          setLoadingId(v.id);
                          preview.mutate(v.id);
                        }}
                        onAskRestore={() => {
                          setError(null);
                          setConfirmId(v.id);
                        }}
                        onCancelRestore={() => setConfirmId(null)}
                        onConfirmRestore={() => restore.mutate(v.id)}
                        canRestore={canRestore}
                      />
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          )}

          {error && (
            <p className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionRow({
  version,
  older,
  firstOfDay,
  isCurrent,
  isViewing,
  isConfirming,
  isPending,
  isOpening,
  onView,
  onAskRestore,
  onCancelRestore,
  onConfirmRestore,
  canRestore,
}: {
  version: WorkflowVersionRow;
  /** The version saved just before this one, to show only what differs. */
  older: WorkflowVersionRow | null;
  /** The author always shows on a day's first row, then only when it changes. */
  firstOfDay: boolean;
  isCurrent: boolean;
  isViewing: boolean;
  isConfirming: boolean;
  isPending: boolean;
  isOpening: boolean;
  onView: () => void;
  onAskRestore: () => void;
  onCancelRestore: () => void;
  onConfirmRestore: () => void;
  /** False for standard users: they get View only. */
  canRestore: boolean;
}) {
  const author = authorPresentation(version);
  // Prompt loss is the destructive-rewrite signature this feature exists to
  // catch. It gets a badge, not a border: a highlighted row should mean
  // "this is where you are", and having two kinds of highlight at once made
  // the list read as though two rows were both somehow selected.
  const destructive = version.summary?.includes("cleared") ?? false;
  const sameAuthor =
    !firstOfDay && !!older && authorPresentation(older).label === author.label;

  return (
    <li
      className={cn(
        "group border-b border-border px-3 py-2.5 transition-colors last:border-0",
        isViewing ? "bg-amber-500/5" : isCurrent ? "bg-accent/5" : "hover:bg-surface-hover",
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
            author.tint,
          )}
          title={author.title}
        >
          <author.Icon size={12} />
        </span>

        <div className="min-w-0 flex-1">
          {/* What changed leads: it is what someone scans this list for. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-[11px] text-text-muted">v{version.version}</span>
            <span className="text-[13px] font-medium text-text-primary">
              {describeChange(version, older)}
            </span>
            {isCurrent && <RowBadge tone="accent">Current</RowBadge>}
            {isViewing && (
              <RowBadge tone="amber">
                <Eye size={9} />
                Viewing
              </RowBadge>
            )}
            {destructive && (
              <RowBadge tone="amber">
                <TriangleAlert size={9} />
                Prompts Lost
              </RowBadge>
            )}
          </div>
          <p className="mt-0.5 truncate text-[11px] text-text-muted">
            <span title={`${formatWhen(version.createdAt)} · ${relativeWhen(version.createdAt)}`}>
              {formatTime(version.createdAt)}
            </span>
            {!sameAuthor && <> · {author.label}</>}
            {" · "}
            {version.nodeCount} nodes
          </p>
        </div>

        {!isCurrent && !isConfirming && (
          // Shown on hover or keyboard focus, so forty rows don't mean
          // eighty buttons. The row being viewed keeps Restore visible.
          <div
            className={cn(
              "flex shrink-0 items-center gap-1 transition-opacity",
              isViewing ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100",
            )}
          >
            {!isViewing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onView}
                disabled={isOpening}
                className="h-7 gap-1.5 px-2 text-xs"
                title={`Show v${version.version} on the canvas without changing anything`}
              >
                {isOpening ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
                View
              </Button>
            )}
            {canRestore && (
              <Button
                size="sm"
                variant="outline"
                onClick={onAskRestore}
                className="h-7 gap-1.5 px-2 text-xs"
                title={`Replace the current workflow with v${version.version}`}
              >
                <RotateCcw size={11} />
                Restore
              </Button>
            )}
          </div>
        )}
      </div>

      {isConfirming && (
        <div className="ml-9 mt-2 flex items-center gap-3 rounded-md border border-border bg-background px-2.5 py-2">
          <p className="flex-1 text-[11px] text-text-secondary">
            Replace what is on the canvas with v{version.version}? The current
            version stays in this list, so you can restore back.
          </p>
          <Button size="sm" variant="ghost" autoFocus onClick={onCancelRestore} disabled={isPending} className="h-7 px-2 text-xs">
            Cancel
          </Button>
          <Button size="sm" onClick={onConfirmRestore} disabled={isPending} className="h-7 gap-1.5 px-2 text-xs">
            {isPending && <Loader2 size={11} className="animate-spin" />}
            Restore
          </Button>
        </div>
      )}
    </li>
  );
}

function RowBadge({ tone, children }: { tone: "accent" | "amber"; children: ReactNode }) {
  return (
    <span
      className={cn(
        "flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
        tone === "accent"
          ? "bg-accent/10 text-accent"
          : "bg-amber-500/10 text-amber-700 dark:text-amber-300",
      )}
    >
      {children}
    </span>
  );
}

const STATUS_WORD: Record<WorkflowVersionRow["status"], string> = {
  draft: "Draft",
  active: "Active",
  paused: "Paused",
};

/**
 * One line saying what this save changed. The server's summary only knows
 * about nodes, edges and prompts, and says "no structural change" for
 * everything else (a moved node, a changed setting, a rename), which read
 * as "nothing happened". Rename and status are worked out here by comparing
 * with the version before.
 */
function describeChange(v: WorkflowVersionRow, older: WorkflowVersionRow | null): string {
  if (v.summary === "Created") return "Created";
  if (v.summary?.startsWith("Restored")) return v.summary;
  // The oldest kept entry: its summary is only a node count, already on the
  // second line.
  if (!older) return "Earliest saved version";

  const parts: string[] = [];
  if (v.name !== older.name) parts.push(`Renamed to "${v.name}"`);
  if (v.status !== older.status) parts.push(`Set to ${STATUS_WORD[v.status]}`);
  if (v.summary && v.summary !== "no structural change") parts.push(v.summary);
  if (parts.length === 0) parts.push("Moved nodes or changed settings");
  return parts.join(" · ");
}

/** Newest first, split into days, each row paired with the one saved before it. */
function groupByDay(versions: WorkflowVersionRow[]) {
  const days: Array<{
    key: string;
    label: string;
    rows: Array<{ version: WorkflowVersionRow; older: WorkflowVersionRow | null; firstOfDay: boolean }>;
  }> = [];
  versions.forEach((version, i) => {
    const d = new Date(version.createdAt);
    const key = d.toDateString();
    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, label: dayLabel(d), rows: [] };
      days.push(day);
    }
    day.rows.push({ version, older: versions[i + 1] ?? null, firstOfDay: day.rows.length === 0 });
  });
  return days;
}

function dayLabel(d: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/**
 * "22 Sept 2026, 18:29". Day and month spelled rather than numeric, because
 * 09/10 reads as two different dates either side of the Atlantic and this list
 * is the thing someone scans when they half-remember when a change went in.
 */
function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "2 days ago". Sits beside the exact date, never instead of it. */
function relativeWhen(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const months = Math.round(days / 30);
  return `${months} ${months === 1 ? "month" : "months"} ago`;
}

/**
 * "Created by X · Last edited by Y", read from the oldest and newest history
 * entries. The server writes a "Created" entry when a workflow is made (must
 * match WORKFLOW_CREATED_SUMMARY in server/src/services/workflow-versions.ts);
 * workflows made before that, or whose first entry has aged out, say so
 * rather than naming whoever happened to save first.
 */
function CreatedAndEdited({ versions }: { versions: WorkflowVersionRow[] }) {
  const newest = versions[0]!;
  const oldest = versions[versions.length - 1]!;
  const hasCreated = oldest.summary === "Created";
  const editedSince = versions.length > 1 || !hasCreated;
  return (
    <p className="mt-3 flex flex-wrap gap-x-1.5 text-xs text-text-secondary">
      <span>
        {hasCreated ? (
          <>
            Created by <span className="font-medium text-text-primary">{authorPresentation(oldest).label}</span>
            {" · "}
            <span title={formatWhen(oldest.createdAt)}>{relativeWhen(oldest.createdAt)}</span>
          </>
        ) : (
          "Created before version history began"
        )}
      </span>
      <span className="text-text-muted">|</span>
      <span>
        {editedSince ? (
          <>
            Last edited by <span className="font-medium text-text-primary">{authorPresentation(newest).label}</span>
            {" · "}
            <span title={formatWhen(newest.createdAt)}>{relativeWhen(newest.createdAt)}</span>
          </>
        ) : (
          "Not edited since"
        )}
      </span>
    </p>
  );
}

/** Icon, tint and label for who made a change. */
function authorPresentation(version: WorkflowVersionRow) {
  if (version.authorType === "agent") {
    return {
      Icon: Bot,
      tint: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
      title: "Changed by an agent",
      label: version.authorLabel
        ? `${version.authorLabel} (agent)`
        : "Agent",
    };
  }
  if (version.authorType === "system") {
    return {
      Icon: Server,
      tint: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400",
      title: "Recorded by the platform",
      label: version.authorLabel ?? "Platform",
    };
  }
  return {
    Icon: User,
    tint: "bg-accent/10 text-accent",
    title: "Changed by a person",
    label: version.authorLabel ?? "Canvas",
  };
}
